import { CommonModule } from '@angular/common';
import { Component, effect, inject } from '@angular/core';
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialog } from '@angular/material/dialog';
import { MatInputModule } from '@angular/material/input';
import { IdDTO, Mobile, PersonalInfo, RegistrationDTO } from '@core';
import { RegistrationStore } from '@features/registration/store/registration.store';
import { CancelButton } from '@shared/components/cancel-button/cancel-button';
import { ErrorDialogComponent } from '@shared/components/error-dialog/error-dialog.component';
import { PrimaryButtonComponent } from '@shared/components/primary-button/pirmary-button.component';
import { Spacer } from '@shared/components/spacer/spacer';
import { formatDateToString } from '@shared/utils/date.util';
import { ContactDetailsForm } from './_components/contact-details-form/contact-details-form';
import { IdentificationForm } from './_components/identification-form/identification-form';
import { PersonalDetailsForm } from './_components/personal-details-form/personal-details-form';

@Component({
  selector: 'app-personal-details',
  imports: [
    CommonModule,
    MatAutocompleteModule,
    MatInputModule,
    ReactiveFormsModule,
    FormsModule,
    MatNativeDateModule,
    MatDatepickerModule,
    PrimaryButtonComponent,
    Spacer,
    PersonalDetailsForm,
    IdentificationForm,
    ContactDetailsForm,
    CancelButton,
  ],
  templateUrl: './personal-details.html',
  styleUrl: './personal-details.scss',
  standalone: true,
})
export class PersonalDetails {
  personalInfoForm?: FormGroup;
  lookupForm?: FormGroup;
  contactDetailsForm?: FormGroup;
  lookupType?: string;
  private _errorDialog = inject(MatDialog);
  store = inject(RegistrationStore);

  constructor() {
    effect(() => {
      const error = this.store?.errorMessage();
      if (error != '') {
        this._errorDialog.open(ErrorDialogComponent, {
          data: { errorMessage: error },
        });
      }
    });
  }

  onPersonalInfoChange(form: FormGroup) {
    this.personalInfoForm = form;
  }

  onLookupFormChange(form: FormGroup) {
    this.lookupForm = form;
  }

  onContactDetailsChange(form: FormGroup) {
    this.contactDetailsForm = form;
  }

  onLookupTypeChange(type: string) {
    this.lookupType = type;
  }

  isFormValid(): boolean {
    return !!(
      this.personalInfoForm?.valid &&
      this.lookupForm?.valid &&
      this.contactDetailsForm?.valid
    );
  }

  initRegisteration(): void {
    if (this.isFormValid()) {
      const formData = {
        personalInfo: this.personalInfoForm?.getRawValue(),
        lookup: this.lookupForm?.getRawValue(),
        contact: this.contactDetailsForm?.getRawValue(),
        lookupType: this.lookupType,
      };

      const { dob, expiryDate, ...lookup } = formData.lookup;

      const RegistrationDTO = {
        termsAccepted: true,
        marketingConsent: true,
        identificationDocument: {
          ...this.getIdentificationNumber(lookup),
        },
        personalInfo: {
          firstName: formData.personalInfo.firstName,
          lastName: formData.personalInfo.lastName,
          dateOfBirth: formatDateToString(new Date(formData.lookup.dob)),
        } as PersonalInfo,
        mobile: {
          number: formData.contact.mobileNr,
          otp: formData.contact.otp,
        } as Mobile,
        email: formData.contact.email,
        platform: 'web',
      } as RegistrationDTO;

      this.store.registerUser(RegistrationDTO);
    }
  }

  getIdentificationNumber(lookupForm: any): IdDTO {
    switch (this.lookupType) {
      case 'I':
        return {
          type: lookupForm.type,
          country: lookupForm.country,
          identificationNumber: lookupForm.idNumber,
        } as IdDTO;
      case 'P':
        return {
          type: lookupForm.type,
          country: lookupForm.country,
          identificationNumber: lookupForm.passportNumber,
        } as IdDTO;
      case 'A':
        return {
          type: lookupForm.type,
          country: lookupForm.country,
          identificationNumber: lookupForm.asylumNumber,
        } as IdDTO;
      default:
        return {
          type: lookupForm.type,
          country: '',
          identificationNumber: '',
        } as IdDTO;
    }
  }
}
